import { useMemo, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { ArrowLeft, Store } from "lucide-react"
import { useGetOrderReviewQuery, useGetGroupOrderQuery } from "shared/redux/endpoints/procurementApprovals"
import { useGetGroupOrderLinesQuery } from "shared/redux/endpoints/procurementPurchases"
import {
  useGetProcurementSuppliersQuery,
  useCreateProcurementSupplierMutation,
} from "shared/redux/endpoints/procurementItems"
import { Page, Card, Button, Loading } from "./ui"
import VendorProposalsPanel from "./components/proposals/VendorProposalsPanel"
import { showToastSuccess, showToastError } from "utilities/toastUtil"
import { getApiErrorMessage } from "./orderSubmitFeedback"
import { toProposalLines } from "./constants"

// Dedicated, focused view of just the Vendor Proposals panel for one order —
// reached from the order list/drawer without opening the full edit form.
// `group`: same page for a Group Order (RFQ against the group's combined lines).
export default function VendorProposalsPageContainer({ group = false }) {
  const { id } = useParams()
  const navigate = useNavigate()
  const orderId = id || null

  const orderQ = useGetOrderReviewQuery(orderId, { skip: !orderId || group })
  const groupQ = useGetGroupOrderQuery(orderId, { skip: !orderId || !group })
  const groupLinesQ = useGetGroupOrderLinesQuery(orderId, { skip: !orderId || !group })
  const order = group ? groupQ.data : orderQ.data
  const isLoading = group ? groupQ.isLoading || groupLinesQ.isLoading : orderQ.isLoading
  const code = group ? order?.code : order?.orCode
  const { data: suppliers } = useGetProcurementSuppliersQuery({ isActive: true })
  const [createSupplier, { isLoading: creatingSupplier }] = useCreateProcurementSupplierMutation()
  // Same cache-lag workaround as the order form: a just-created supplier
  // isn't guaranteed to show up in `suppliers` until its list query refetches.
  const [createdSuppliers, setCreatedSuppliers] = useState([])

  const supplierOptions = useMemo(() => {
    const byId = new Map((suppliers || []).map((s) => [s.id, s]))
    createdSuppliers.forEach((s) => {
      if (!byId.has(s.id)) byId.set(s.id, s)
    })
    return Array.from(byId.values()).map((s) => ({
      value: s.id,
      label: s.name,
      taxCode: s.taxCode,
      email: s.email,
    }))
  }, [suppliers, createdSuppliers])

  const orderLines = useMemo(
    () =>
      toProposalLines(group ? groupLinesQ.data?.lines : order?.lines),
    [group, order, groupLinesQ.data]
  )

  const handleCreateSupplier = async (draft) => {
    try {
      const created = await createSupplier({
        name: draft.name.trim(),
        taxCode: draft.taxCode.trim(),
        email: draft.email.trim() || undefined,
      }).unwrap()
      setCreatedSuppliers((prev) => [...prev, created])
      showToastSuccess("Supplier created.")
      return created
    } catch (e) {
      showToastError(getApiErrorMessage(e, "Couldn't create this supplier. Please try again."))
      throw e
    }
  }

  return (
    <Page
      breadcrumb={["Procurement", group ? "Group Order" : "Order Request", code || id]}
      title="Vendor Proposals"
      subtitle={order?.title}
      actions={
        <Button variant="ghost" size="sm" leftIcon={<ArrowLeft size={14} aria-hidden />} onClick={() => navigate(-1)}>
          Back
        </Button>
      }
    >
      {isLoading ? (
        <Loading />
      ) : (
        <Card icon={<Store aria-hidden />} title="Vendor Proposals" subtitle={code}>
          <VendorProposalsPanel
            sourceType={group ? "group" : "order"}
            sourceId={orderId}
            orderLines={orderLines}
            supplierOptions={supplierOptions}
            onCreateSupplier={handleCreateSupplier}
            creatingSupplier={creatingSupplier}
          />
        </Card>
      )}
    </Page>
  )
}
